import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowUpRight,
  Boxes,
  ExternalLink,
  Package,
  PackageX,
  RefreshCw,
  Search,
  Store,
  TrendingUp,
} from 'lucide-react';
import {
  Alert,
  Button,
  EmptyState,
  Input,
  Loading,
  PageHeader,
  Panel,
  PanelBody,
  SectionLabel,
  StatusPill,
} from '../ui/index.js';
import { fetchOutOfStock, type OutOfStockItemDto } from '../api.js';
import { SubBrandPills } from '../components/SubBrandPills.js';

/**
 * S31 — Ruptures & disponibilité.
 *
 * Every product we cannot sell right now, with the two questions that
 * matter commercially:
 *  - can we restock it? (our depot lists say it is available)
 *  - is a competitor selling it while we are dry? (the opportunity)
 */

const PAGE_SIZE = 50;

function tnd(millimes: number | string | null): string {
  if (millimes === null) return '—';
  const n = typeof millimes === 'string' ? Number(millimes) : millimes;
  if (!Number.isFinite(n)) return '—';
  return (n / 1000).toFixed(3);
}

function freshnessLabel(fetchedAt: string | null): string {
  if (fetchedAt === null) return 'jamais collecté';
  const days = Math.floor((Date.now() - new Date(fetchedAt).getTime()) / 86_400_000);
  if (days <= 0) return 'collecté aujourd’hui';
  if (days === 1) return 'collecté hier';
  return `collecté il y a ${days} j`;
}

function isStale(fetchedAt: string | null): boolean {
  if (fetchedAt === null) return true;
  const at = new Date(fetchedAt).getTime();
  if (!Number.isFinite(at)) return true;
  return Date.now() - at > 7 * 86_400_000;
}

function AvailabilityBadges({ item }: { readonly item: OutOfStockItemDto }): React.ReactElement {
  const wholesalerNames = item.wholesalerAvailable.map((w) => w.sourceLabel).filter(Boolean);
  const allWholesalerNames = (item.wholesalers ?? []).map((w) => w.sourceLabel).filter(Boolean);

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {item.wholesalerAvailable.length > 0 ? (
        <StatusPill tone="ok" title={`Disponible chez nos grossistes B2B : ${wholesalerNames.join(', ')}`}>
          <Boxes className="h-3 w-3" aria-hidden="true" />
          Grossiste : {wholesalerNames.join(', ')} (dispo)
        </StatusPill>
      ) : allWholesalerNames.length > 0 ? (
        <StatusPill tone="ko" title={`En rupture chez nos grossistes : ${allWholesalerNames.join(', ')}`}>
          Grossiste : rupture ({allWholesalerNames.join(', ')})
        </StatusPill>
      ) : item.depotAvailable.length > 0 ? (
        <StatusPill tone="ok" title="Notre dépôt le propose encore disponible">
          <Boxes className="h-3 w-3" aria-hidden="true" />
          Dépôt : {item.depotAvailable.length} dispo
        </StatusPill>
      ) : item.depot.length > 0 ? (
        <StatusPill tone="ko" title="Le dépôt liste le produit mais l’indique indisponible">
          Dépôt : indisponible
        </StatusPill>
      ) : (
        <StatusPill tone="info" title="Aucun grossiste ne référence actuellement ce produit">
          Grossiste : non référencé
        </StatusPill>
      )}

      {item.soldByCompetitors > 0 ? (
        <StatusPill
          tone="ok"
          title={`${item.soldByCompetitors} concurrent(s) le publient en stock, dont ${item.soldByCompetitorsFresh} collecté(s) il y a moins de 7 jours`}
        >
          <TrendingUp className="h-3 w-3" aria-hidden="true" />
          {item.soldByCompetitors} concurrent(s) en stock
          {item.soldByCompetitorsFresh > 0 ? ` (${item.soldByCompetitorsFresh} frais)` : ''}
        </StatusPill>
      ) : (
        <StatusPill tone="info" title="Aucun concurrent ne le vend en ce moment">
          Concurrence : 0
        </StatusPill>
      )}
    </div>
  );
}

function CompetitorList({ item }: { readonly item: OutOfStockItemDto }): React.ReactElement {
  const rows = [...item.competitors].sort((a, b) => {
    const aIn = a.inStock === true ? 0 : 1;
    const bIn = b.inStock === true ? 0 : 1;
    return aIn - bIn;
  });
  if (rows.length === 0) {
    return <p className="text-xs text-ink-faint">Aucun concurrent ne publie ce produit.</p>;
  }
  return (
    <ul className="flex flex-wrap gap-1.5">
      {rows.map((o) => {
        const stale = isStale(o.fetchedAt);
        const inStock = o.inStock === true;
        const classes = inStock
          ? stale
            ? 'border-emerald-300 bg-emerald-50/80 text-emerald-950 hover:bg-emerald-100 hover:border-emerald-400'
            : 'border-emerald-400 bg-emerald-100 text-emerald-950 hover:bg-emerald-200 hover:border-emerald-500'
          : 'border-rose-200 bg-rose-50 text-rose-950 hover:bg-rose-100 hover:border-rose-300';
        const label = `${o.sourceLabel} — ${
          inStock ? 'en stock' : 'en rupture'
        }${o.priceTtc !== null ? ` — ${tnd(o.priceTtc)} TND` : ''} — ${freshnessLabel(o.fetchedAt)}${
          stale ? ' (donnée ancienne, à recouper)' : ''
        }`;
        return (
          <li key={`${o.sourceKey}-${o.gtin ?? o.url ?? ''}`}>
            {o.url !== null ? (
              <a
                href={o.url}
                target="_blank"
                rel="noreferrer"
                className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold transition-all shadow-2xs ${classes}`}
                title={label}
              >
                <Store className={`h-3 w-3 shrink-0 ${inStock ? 'text-emerald-700' : 'text-rose-600'}`} aria-hidden="true" />
                <span>{o.sourceLabel}</span>
                <span className={inStock ? 'font-bold text-emerald-900' : 'font-bold text-rose-700'}>
                  {inStock ? '· en stock' : '· rupture'}
                </span>
                {o.priceTtc !== null ? <span className="font-bold tabular-nums">· {tnd(o.priceTtc)}</span> : ''}
                <ArrowUpRight className="h-2.5 w-2.5 shrink-0 opacity-70" aria-hidden="true" />
              </a>
            ) : (
              <span
                className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold shadow-2xs ${classes}`}
                title={label}
              >
                <Store className={`h-3 w-3 shrink-0 ${inStock ? 'text-emerald-700' : 'text-rose-600'}`} aria-hidden="true" />
                <span>{o.sourceLabel}</span>
                <span className={inStock ? 'font-bold text-emerald-900' : 'font-bold text-rose-700'}>
                  {inStock ? '· en stock' : '· rupture'}
                </span>
                {o.priceTtc !== null ? <span className="font-bold tabular-nums">· {tnd(o.priceTtc)}</span> : ''}
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function WholesalerList({ item }: { readonly item: OutOfStockItemDto }): React.ReactElement {
  const rows =
    item.wholesalers && item.wholesalers.length > 0
      ? [...item.wholesalers].sort((a, b) => {
          const aIn = a.inStock === true ? 0 : 1;
          const bIn = b.inStock === true ? 0 : 1;
          return aIn - bIn;
        })
      : [];

  if (rows.length === 0) {
    if (item.depot.length > 0) {
      return (
        <ul className="flex flex-wrap gap-1.5">
          {item.depot.map((o) => (
            <li key={`${o.sourceKey}-${o.gtin ?? ''}`}>
              <span
                className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold shadow-2xs ${
                  o.inStock === true
                    ? 'border-emerald-300 bg-emerald-50 text-emerald-950'
                    : 'border-rose-200 bg-rose-50 text-rose-950'
                }`}
                title={`${o.sourceLabel}${o.priceTtc !== null ? ` — prix achat ${tnd(o.priceTtc)} TND` : ''}${o.stockQty !== null ? ` — stock ${o.stockQty}` : ''}`}
              >
                <Boxes className={`h-3 w-3 shrink-0 ${o.inStock === true ? 'text-emerald-700' : 'text-rose-600'}`} aria-hidden="true" />
                <span>{o.sourceLabel}</span>
                <span className={o.inStock === true ? 'font-bold text-emerald-800' : 'font-bold text-rose-700'}>
                  {o.inStock === true ? '· dispo' : '· indisponible'}
                </span>
                {o.stockQty !== null ? <span className="font-bold tabular-nums">· {o.stockQty} u.</span> : ''}
                {o.priceTtc !== null ? <span className="tabular-nums">· {tnd(o.priceTtc)}</span> : ''}
              </span>
            </li>
          ))}
        </ul>
      );
    }
    return <p className="text-xs text-ink-faint">Aucun grossiste B2B ne référence ce produit.</p>;
  }

  const salePrice = Number(item.salePriceTtc ?? item.regularPriceTtc ?? 0);

  return (
    <ul className="grid gap-2 sm:grid-cols-2">
      {rows.map((w) => {
        const inStock = w.inStock === true;
        const vat = w.vatRate ?? 19;
        const nominalTtc =
          w.priceTtc !== null
            ? w.priceTtc
            : w.priceHt !== null && w.priceHt !== undefined
              ? Math.round(w.priceHt * (1 + vat / 100))
              : null;
        const effectiveHt = w.unitPriceRealHt ?? w.priceHt ?? null;
        const effectiveTtc =
          effectiveHt !== null
            ? Math.round(effectiveHt * (1 + vat / 100))
            : nominalTtc;
        const promo =
          w.promoLabel ??
          (w.promoBuyQty && w.promoFreeQty
            ? `${w.promoBuyQty}+${w.promoFreeQty}`
            : w.promoPercent
              ? `-${w.promoPercent}%`
              : null);

        let marginPercent: number | null = null;
        let markupPercent: number | null = null;
        if (effectiveTtc !== null && effectiveTtc > 0 && salePrice > 0) {
          marginPercent = Math.round(((salePrice - effectiveTtc) / salePrice) * 100);
          markupPercent = Math.round(((salePrice - effectiveTtc) / effectiveTtc) * 100);
        }

        return (
          <li
            key={`${w.sourceKey}-${w.gtin ?? w.url ?? ''}`}
            className={`rounded-xl border p-2.5 text-xs shadow-2xs transition-all ${
              inStock
                ? 'border-emerald-300 bg-emerald-50/60 hover:bg-emerald-50'
                : 'border-slate-200 bg-slate-50/70 opacity-75'
            }`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="font-bold text-ink">{w.sourceLabel}</span>
                  <span
                    className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                      inStock
                        ? 'bg-emerald-100 text-emerald-900 border border-emerald-300'
                        : 'bg-rose-100 text-rose-800 border border-rose-200'
                    }`}
                  >
                    {inStock ? 'En stock' : 'Rupture'}
                    {w.stockQty !== null && inStock ? ` (${w.stockQty} u.)` : ''}
                  </span>
                  {promo ? (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-900 border border-amber-300">
                      Offre : {promo}
                    </span>
                  ) : null}
                </div>
                {w.b2bProductName ? (
                  <p className="text-[11px] text-ink-muted truncate mt-0.5" title={w.b2bProductName}>
                    {w.b2bProductName}
                  </p>
                ) : null}
              </div>

              {w.url ? (
                <a
                  href={w.url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-ink-ghost hover:text-ink shrink-0 p-1 rounded-md hover:bg-cream"
                  title="Ouvrir portail B2B"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              ) : null}
            </div>

            <div className="mt-2 flex items-center justify-between border-t border-border/60 pt-1.5 text-[11px]">
              <div>
                <span className="text-ink-soft">Prix Achat : </span>
                <span className="font-bold font-mono text-ink">
                  {effectiveTtc !== null ? `${tnd(effectiveTtc)} TND TTC` : '—'}
                </span>
                {effectiveHt !== null && effectiveTtc !== null && (
                  <span className="text-[10px] text-ink-ghost ml-1">
                    ({tnd(effectiveHt)} HT)
                  </span>
                )}
              </div>

              {marginPercent !== null ? (
                <div className="text-right">
                  <span className="text-ink-soft">Marge : </span>
                  <span
                    className={`font-bold font-mono ${
                      marginPercent >= 25
                        ? 'text-emerald-700'
                        : marginPercent >= 15
                          ? 'text-amber-700'
                          : 'text-rose-700'
                    }`}
                  >
                    {marginPercent > 0 ? `+${marginPercent}%` : `${marginPercent}%`}
                  </span>
                  {markupPercent !== null && (
                    <span className="text-[10px] text-ink-ghost ml-1" title="Taux de marque (markup)">
                      (mkp +{markupPercent}%)
                    </span>
                  )}
                </div>
              ) : null}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

export function OutOfStockPage(): React.ReactElement {
  const [search, setSearch] = useState('');
  const [brand, setBrand] = useState('all');
  const [excludedSubBrands, setExcludedSubBrands] = useState<string[]>([]);
  const [restockableOnly, setRestockableOnly] = useState(false);
  const [competitorOnly, setCompetitorOnly] = useState(false);
  const [page, setPage] = useState(0);

  const query = useQuery({
    queryKey: ['out-of-stock', search, brand, excludedSubBrands, restockableOnly, competitorOnly, page],
    queryFn: () =>
      fetchOutOfStock({
        ...(search.trim() !== '' ? { q: search.trim() } : {}),
        ...(brand !== 'all' ? { brand } : {}),
        ...(excludedSubBrands.length > 0 ? { excludedSubBrands: excludedSubBrands.join(',') } : {}),
        ...(restockableOnly ? { restockable: '1' } : {}),
        ...(competitorOnly ? { competitorStock: '1' } : {}),
        limit: PAGE_SIZE,
        skip: page * PAGE_SIZE,
      }),
  });

  const counts = query.data?.counts;
  const items = query.data?.items ?? [];
  const brands = query.data?.brands ?? [];
  const total = query.data?.total ?? 0;
  const maxPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);

  return (
    <div>
      <PageHeader
        index="S31"
        title="Ruptures & disponibilité"
        purpose="Les produits que nous ne pouvons pas vendre : notre dépôt peut-il les fournir, et un concurrent les vend-il pendant ce temps ? Chaque rupture vendable est une opportunité."
        meta={[
          { label: 'Ruptures', value: String(counts?.outOfStock ?? 0) },
          { label: 'Réappro', value: String(counts?.restockable ?? 0) },
          { label: 'En concurrence', value: String(counts?.competitorOpportunityFresh ?? 0) },
        ]}
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={() => void query.refetch()}
            disabled={query.isFetching}
          >
            <RefreshCw className={`h-4 w-4 ${query.isFetching ? 'animate-spin' : ''}`} aria-hidden="true" />
            Actualiser
          </Button>
        }
      />

      <div className="space-y-4">
        {/* Filters */}
        <Panel>
          <PanelBody className="flex flex-wrap items-center gap-3">
            <div className="relative min-w-0 flex-1 sm:max-w-xs">
              <label className="sr-only" htmlFor="oos-search">
                Rechercher un produit
              </label>
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-ghost"
                aria-hidden="true"
              />
              <Input
                id="oos-search"
                type="search"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(0);
                }}
                placeholder="Nom ou SKU…"
                className="h-9 pl-9"
              />
            </div>

            <label className="flex items-center gap-2">
              <span className="eyebrow text-ink-faint">Marque</span>
              <select
                value={brand}
                onChange={(e) => {
                  setBrand(e.target.value);
                  setExcludedSubBrands([]);
                  setPage(0);
                }}
                className="h-9 rounded-lg border border-border bg-card px-2.5 text-sm font-medium text-ink shadow-xs"
              >
                <option value="all">Toutes</option>
                {brands.map((b) => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
              </select>
            </label>
            <div className="w-full">
              <SubBrandPills
                brand={brand}
                excludedSubBrands={excludedSubBrands}
                onChange={(ex) => {
                  setExcludedSubBrands(ex);
                  setPage(0);
                }}
              />
            </div>

            <label className="flex items-center gap-2 text-xs font-semibold text-ink-soft">
              <input
                type="checkbox"
                checked={restockableOnly}
                onChange={(e) => {
                  setRestockableOnly(e.target.checked);
                  setPage(0);
                }}
                className="h-4 w-4 rounded border-border-strong accent-herb-600"
              />
              Réapprovisionnable par le dépôt
            </label>

            <label className="flex items-center gap-2 text-xs font-semibold text-ink-soft">
              <input
                type="checkbox"
                checked={competitorOnly}
                onChange={(e) => {
                  setCompetitorOnly(e.target.checked);
                  setPage(0);
                }}
                className="h-4 w-4 rounded border-border-strong accent-herb-600"
              />
              Vendu par un concurrent
            </label>
          </PanelBody>
        </Panel>

        {counts !== undefined && counts.competitorOpportunity > 0 && (
          <Alert tone="warn" title="Ce que cela veut dire">
            <p>
              <strong>{counts.competitorOpportunity}</strong> produit(s) en rupture chez nous sont
              publiés <strong>en stock</strong> par au moins un concurrent —{' '}
              {counts.competitorOpportunityFresh} d&apos;entre eux sur des collectes de moins de
              7 jours (les {counts.competitorOpportunity - counts.competitorOpportunityFresh} autres
              reposent sur une collecte plus ancienne, à recouper).
            </p>
            <p className="mt-1.5">
              Cela prouve que <strong>ce produit se vend chez quelqu&apos;un</strong> — pas que
              la demande nous revient. Le réapprovisionnement se décide produit par produit, en
              tenant compte de la marge, du stock du dépôt et du coût de remise en état.
            </p>
          </Alert>
        )}

        {/* List */}
        <Panel>
          <PanelBody className="space-y-3">
            <SectionLabel>
              {total} produit(s) en rupture {maxPage > 0 ? `· page ${page + 1}/${maxPage + 1}` : ''}
            </SectionLabel>

            {query.isLoading && <Loading label="Analyse des ruptures…" />}
            {query.isError && (
              <Alert tone="alarm" title="Liste indisponible">
                {String(query.error.message)}
              </Alert>
            )}

            {query.data !== undefined && items.length === 0 && (
              <EmptyState
                icon={<PackageX className="h-6 w-6" aria-hidden="true" />}
                title="Aucune rupture ne correspond"
                hint="Ajustez la recherche, la marque ou les filtres — ou le catalogue est simplement complet."
              />
            )}

            {items.length > 0 && (
              <ul className="space-y-2">
                {items.map((item) => (
                  <li
                    key={item.id}
                    className="rounded-xl border border-border bg-card p-3 shadow-xs"
                  >
                    <div className="flex flex-wrap items-start gap-3">
                      {item.image !== null ? (
                        <img
                          src={item.image}
                          alt=""
                          width={112}
                          height={112}
                          loading="lazy"
                          className="h-24 w-24 sm:h-28 sm:w-28 shrink-0 rounded-2xl border border-border bg-white object-contain p-1.5 shadow-xs"
                        />
                      ) : (
                        <span
                          className="grid h-24 w-24 sm:h-28 sm:w-28 shrink-0 place-items-center rounded-2xl border border-dashed border-border bg-cream text-ink-ghost"
                          aria-hidden="true"
                        >
                          <Package className="h-6 w-6" />
                        </span>
                      )}

                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-bold text-ink" title={item.name}>
                          {item.name}
                        </p>
                        <p className="font-mono text-[11px] text-ink-faint">
                          {item.sku}
                          {item.brand !== null ? ` · ${item.brand}` : ''}
                          {item.gtin !== null ? ` · ${item.gtin}` : ''}
                        </p>
                        <div className="mt-1.5">
                          <AvailabilityBadges item={item} />
                        </div>
                      </div>

                      <div className="shrink-0 text-right">
                        <p className="text-xs text-ink-soft">
                          Prix vente{' '}
                          <strong className="tabular">
                            {tnd(item.salePriceTtc ?? item.regularPriceTtc)}
                          </strong>{' '}
                          TND
                        </p>
                        {item.purchasePriceHt !== null && (
                          <p className="text-[11px] text-ink-faint">
                            Achat {tnd(item.purchasePriceHt)} TND
                          </p>
                        )}
                        <p className="text-[11px] text-ink-faint">
                          Stock : {item.stockQty ?? '—'}
                        </p>
                      </div>
                    </div>

                    <div className="mt-2.5 grid gap-2.5 border-t border-border pt-2.5 lg:grid-cols-2">
                      <div>
                        <p className="eyebrow mb-1 text-ink-faint">Grossistes B2B & Réappro</p>
                        <WholesalerList item={item} />
                      </div>
                      <div>
                        <p className="eyebrow mb-1 text-ink-faint">Concurrents en ligne</p>
                        <CompetitorList item={item} />
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}

            {maxPage > 0 && (
              <div className="flex items-center justify-between border-t border-border pt-3 text-sm">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  Précédent
                </Button>
                <span className="text-xs font-semibold text-ink-soft">
                  Page {page + 1} / {maxPage + 1}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={page >= maxPage}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Suivant
                </Button>
              </div>
            )}
          </PanelBody>
        </Panel>

        <p className="flex items-start gap-2 text-xs text-ink-soft">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-clay-600" aria-hidden="true" />
          <span>
            « En stock » chez un concurrent vient de <strong>sa publication</strong>, relevée à
            chaque crawl : survolez une pastille pour voir sa date de collecte. Les produits
            volontairement non vendus (masqués en boutique, packs) ne sont pas comptés comme
            des ruptures. Les prix affichés sont ceux publiés par la source, en TND.
          </span>
        </p>
      </div>
    </div>
  );
}
